import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { Copy, Wallet } from "lucide-react";
import { Badge, type BadgeVariant } from "@/shared/ui/Badge";
import { Skeleton } from "@/shared/ui/Skeleton";
import { SortHeader } from "@/shared/ui/SortHeader";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/shared/ui/Table";
import { useToast } from "@/shared/ui/Toast";
import { relativeTime } from "@/shared/lib/time";
import { formatDate, formatSom, formatUsd } from "@/features/metrics/format";
import type { RenewalOrdering, RenewalRow } from "./types";

interface Props {
  rows: RenewalRow[];
  loading: boolean;
  ordering: RenewalOrdering;
  onSortChange: (ordering: RenewalOrdering) => void;
  onOpen: (userId: number) => void;
}

const COLS = 6;
const TH = "h-11 px-4 align-middle text-xs font-medium text-grey-500";

export function RenewalsTable({ rows, loading, ordering, onSortChange, onOpen }: Props) {
  const { t } = useTranslation();
  const sort = { ordering, onSortChange, className: TH };
  return (
    <>
      {/* Phones: one card per merchant (sorting moves to MobileSort above the list). */}
      <ul className="divide-y divide-grey-200 md:hidden">
        {loading ? (
          Array.from({ length: 6 }, (_, i) => <RenewalCardSkeleton key={i} />)
        ) : rows.length === 0 ? (
          <li>
            <RenewalsEmpty />
          </li>
        ) : (
          rows.map((r) => <RenewalCard key={r.premium_id} row={r} onOpen={onOpen} />)
        )}
      </ul>

      <div className="hidden overflow-x-auto md:block">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortHeader {...sort} field="user__first_name" firstDir="asc" label={t("renewals.colMerchant")} />
              <SortHeader {...sort} field="is_free" firstDir="asc" label={t("renewals.colPremium")} />
              <SortHeader {...sort} field="end_date" firstDir="asc" label={t("renewals.colTerm")} />
              <SortHeader
                {...sort}
                field="active_agreements"
                align="right"
                label={t("renewals.colAgreements")}
                hint={t("renewals.agreementsHint")}
              />
              <SortHeader
                {...sort}
                field="accepted_clients_count"
                align="right"
                label={t("renewals.colClients")}
                hint={t("renewals.clientsHint")}
              />
              <SortHeader
                {...sort}
                field="last_login_at"
                label={t("renewals.colActivity")}
                hint={t("renewals.activityHint")}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: COLS }, (_, j) => (
                    <TableCell key={j} className="px-4 py-3">
                      <Skeleton className={`h-4 w-24 ${j === 3 || j === 4 ? "ml-auto" : ""}`} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLS} className="p-0">
                  <RenewalsEmpty />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => <RenewalRowView key={r.premium_id} row={r} onOpen={onOpen} />)
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function RenewalsEmpty() {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-pill bg-grey-50 text-grey-400">
        <Wallet size={28} />
      </div>
      <div className="text-[14px] font-semibold text-grey-700">{t("renewals.empty")}</div>
      <div className="mt-1 text-[13px] text-grey-500">{t("renewals.emptyHint")}</div>
    </div>
  );
}

// Everything a row shows, derived once for both the desktop row and the phone card.
function useRenewalView(r: RenewalRow) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  function copyPhone(e: MouseEvent) {
    e.stopPropagation();
    void navigator.clipboard?.writeText(r.phone_number).then(() => showToast(t("renewals.copied")));
  }

  // Term badge: expiring rows get urgency colours; expired rows are always danger.
  const expired = r.days_left < 0;
  let termVariant: BadgeVariant = "muted";
  let termText: string;
  if (expired) {
    termVariant = "danger";
    termText = t("renewals.endedAgo", { n: Math.abs(r.days_left) });
  } else {
    if (r.days_left <= 3) termVariant = "danger";
    else if (r.days_left <= 7) termVariant = "warning";
    termText = r.days_left === 0 ? t("renewals.endsToday") : t("renewals.daysLeft", { n: r.days_left });
  }

  // Activity bar: green >= 15 active days in 30, yellow 5..14, grey otherwise.
  const activeDays = r.active_days_30;
  const barColor = activeDays >= 15 ? "bg-green" : activeDays >= 5 ? "bg-warning" : "bg-grey-300";
  const barPct = Math.min(100, Math.max(0, (activeDays / 30) * 100));
  const lastLogin = r.last_login_at ? parseISO(r.last_login_at) : null;

  // Portfolio of the active agreements in USD; so'm only when the backend had no USD rate.
  const portfolio = r.portfolio_usd !== null ? formatUsd(r.portfolio_usd) : formatSom(r.portfolio_som, t);

  return { t, copyPhone, termVariant, termText, activeDays, barColor, barPct, lastLogin, portfolio };
}

function RenewalCardSkeleton() {
  return (
    <li className="px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="mt-2 h-3.5 w-28" />
        </div>
        <Skeleton className="h-5 w-16 rounded-pill" />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-11 rounded-[10px]" />
        ))}
      </div>
    </li>
  );
}

function RenewalCard({ row: r, onOpen }: { row: RenewalRow; onOpen: (id: number) => void }) {
  const v = useRenewalView(r);
  const { t } = v;
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(r.user_id)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(r.user_id);
          }
        }}
        aria-label={t("renewals.openMerchant", { name: r.full_name })}
        className="block cursor-pointer px-4 py-4 transition-colors active:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green/40"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-[15px] font-bold text-grey-900">{r.full_name}</div>
            <div className="mt-1 flex items-center gap-1">
              <a
                href={`tel:${r.phone_number.replace(/\s/g, "")}`}
                onClick={(e) => e.stopPropagation()}
                className="tabular-nums text-[13.5px] text-blue"
              >
                {r.phone_number}
              </a>
              <button
                type="button"
                onClick={v.copyPhone}
                aria-label={t("renewals.copy")}
                className="-my-2 flex h-9 w-9 items-center justify-center rounded-lg text-grey-400 active:bg-grey-100"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <Badge variant={v.termVariant} className="flex-none">
            {v.termText}
          </Badge>
        </div>

        <div className="mt-2 flex items-center gap-2 text-[12px] tabular-nums text-grey-500">
          <Badge variant={r.is_free ? "muted" : "default"}>
            {r.is_free ? t("renewals.free") : t("renewals.paid")}
          </Badge>
          <span className="truncate">
            {formatDate(r.start_date)} → {formatDate(r.end_date)}
          </span>
        </div>

        {/* Activity gets the widest tile — its relative-time text is the longest value. */}
        <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)] gap-2">
          <div className="min-w-0 rounded-[10px] bg-grey-50 px-2.5 py-2">
            <dt className="truncate text-[11px] font-medium text-grey-500">{t("renewals.colAgreements")}</dt>
            <dd className="mt-0.5 tabular-nums">
              <span
                className={`text-[15px] font-semibold ${r.active_agreements > 0 ? "text-grey-900" : "text-grey-400"}`}
              >
                {r.active_agreements}
              </span>
              <span className="ml-1 text-[11px] text-grey-400">{t("renewals.activeShort")}</span>
              <div className="truncate text-[11.5px] text-grey-500">{v.portfolio}</div>
            </dd>
          </div>
          <div className="min-w-0 rounded-[10px] bg-grey-50 px-2.5 py-2">
            <dt className="truncate text-[11px] font-medium text-grey-500">{t("renewals.colClients")}</dt>
            <dd className="mt-0.5 tabular-nums">
              <span
                className={`text-[15px] font-semibold ${r.accepted_clients_count > 0 ? "text-grey-900" : "text-grey-400"}`}
              >
                {r.accepted_clients_count}
              </span>
              <div className="truncate text-[11.5px] text-grey-500">
                {t("renewals.clientsTotal", { n: r.clients_count })}
              </div>
            </dd>
          </div>
          <div className="min-w-0 rounded-[10px] bg-grey-50 px-2.5 py-2">
            <dt className="truncate text-[11px] font-medium text-grey-500">{t("renewals.colActivity")}</dt>
            <dd className="mt-0.5">
              <div className={`truncate text-[12.5px] ${v.lastLogin ? "text-grey-800" : "text-grey-400"}`}>
                {v.lastLogin ? relativeTime(r.last_login_at, t) : t("renewals.neverLoggedIn")}
              </div>
              <div
                className="mt-1.5 h-1.5 w-full overflow-hidden rounded-pill bg-grey-200"
                title={t("renewals.activeDays", { n: v.activeDays })}
              >
                <div className={`h-full rounded-pill ${v.barColor}`} style={{ width: `${v.barPct}%` }} />
              </div>
            </dd>
          </div>
        </dl>
      </div>
    </li>
  );
}

function RenewalRowView({ row: r, onOpen }: { row: RenewalRow; onOpen: (id: number) => void }) {
  const { t, copyPhone, termVariant, termText, activeDays, barColor, barPct, lastLogin, portfolio } =
    useRenewalView(r);

  return (
    <TableRow
      onClick={() => onOpen(r.user_id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return; // ignore keys from inner links/buttons
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(r.user_id);
        }
      }}
      aria-label={t("renewals.openMerchant", { name: r.full_name })}
      className="cursor-pointer hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green/40"
    >
      <TableCell className="px-4 py-3">
        <div className="font-bold text-grey-900">{r.full_name}</div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <a
            href={`tel:${r.phone_number.replace(/\s/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            className="tabular-nums text-[13px] text-blue hover:underline"
          >
            {r.phone_number}
          </a>
          <button
            type="button"
            onClick={copyPhone}
            title={t("renewals.copy")}
            aria-label={t("renewals.copy")}
            className="rounded p-0.5 text-grey-400 hover:bg-grey-100 hover:text-grey-700"
          >
            <Copy size={13} />
          </button>
        </div>
      </TableCell>

      <TableCell className="px-4 py-3">
        <Badge variant={r.is_free ? "muted" : "default"}>
          {r.is_free ? t("renewals.free") : t("renewals.paid")}
        </Badge>
        <div className="mt-1 text-[12px] tabular-nums text-grey-500">
          {formatDate(r.start_date)} → {formatDate(r.end_date)}
        </div>
      </TableCell>

      <TableCell className="px-4 py-3">
        <Badge variant={termVariant}>{termText}</Badge>
      </TableCell>

      <TableCell className="px-4 py-3 text-right">
        <div className="tabular-nums">
          <span
            className={`text-[15px] font-semibold ${r.active_agreements > 0 ? "text-grey-900" : "text-grey-400"}`}
          >
            {r.active_agreements}
          </span>
          <span className="ml-1 text-[12px] text-grey-400">{t("renewals.activeShort")}</span>
        </div>
        <div className="mt-0.5 text-[12px] tabular-nums text-grey-500">
          {portfolio}
          {r.agreements_30d > 0 && (
            <span className="text-green-dark"> · {t("renewals.new30d", { n: r.agreements_30d })}</span>
          )}
        </div>
      </TableCell>

      <TableCell className="px-4 py-3 text-right tabular-nums">
        <div
          className={`text-[15px] font-semibold ${r.accepted_clients_count > 0 ? "text-grey-900" : "text-grey-400"}`}
        >
          {r.accepted_clients_count}
        </div>
        <div className="mt-0.5 text-[12px] text-grey-500">{t("renewals.clientsTotal", { n: r.clients_count })}</div>
      </TableCell>

      <TableCell className="px-4 py-3">
        <div
          className={`text-[13px] ${lastLogin ? "text-grey-700" : "text-grey-400"}`}
          title={lastLogin ? format(lastLogin, "dd.MM.yyyy HH:mm") : undefined}
        >
          {lastLogin ? relativeTime(r.last_login_at, t) : t("renewals.neverLoggedIn")}
        </div>
        <div
          className="mt-1.5 h-1.5 w-24 overflow-hidden rounded-pill bg-grey-100"
          title={t("renewals.activeDays", { n: activeDays })}
        >
          <div className={`h-full rounded-pill ${barColor}`} style={{ width: `${barPct}%` }} />
        </div>
      </TableCell>
    </TableRow>
  );
}
